import {
  collection,
  CollectionReference,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  Query,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import toast from "react-hot-toast";
import { firestore } from "../firebase";
import { getAllVariantsOfSearchTerm } from "../lib/get-all-variants-of-search-term";
import {
  ImportedInvestorCollection,
  ImportedInvestorEntry,
  InvestorRole,
} from "../types/ImportedInvestorEntry";
import {
  SearchableInvestorEntry,
  SearchableInvestorEntryCollection,
} from "../types/SearchableInvestorEntry";
import {
  mapDocToVCDirectoryMetadata,
  VCDirectoryMetadata,
  VCDirectoryMetadataCollection,
} from "../types/VCDirectoryMetadata";

//// IMPORTED INVESTORS

function mapDocToImportedInvestor(
  id: string,
  data: any,
): ImportedInvestorEntry {
  return {
    id,
    ...data,
  };
}

export const createImportedInvestor = async (
  investor: ImportedInvestorEntry,
) => {
  const investorRef = doc(firestore, ImportedInvestorCollection, investor.id);
  await setDoc(investorRef, investor);
};

export const getInvestorEntryByEmail = async (email: string) => {
  const investorsRef = query(
    collection(firestore, ImportedInvestorCollection),
    where("email", "==", email),
    limit(1),
  );
  const snapshot = await getDocs(investorsRef);
  if (snapshot.empty) {
    return null;
  }
  return mapDocToImportedInvestor(snapshot.docs[0].id, snapshot.docs[0].data());
};

export const getInvestorEntryById = async (id: string) => {
  const investorRef = doc(firestore, ImportedInvestorCollection, id);
  const snapshot = await getDoc(investorRef);
  if (!snapshot.exists()) {
    return null;
  }
  return mapDocToImportedInvestor(snapshot.id, snapshot.data());
};

export async function splitImportedInvestorField(
  targetField: "sectorFocus" | "stageFocus",
  selectedValue: string,
  finalValues: string[],
) {
  const selectedValuesWithVariants = getAllVariantsOfSearchTerm(selectedValue);
  const collectionRef = collection(firestore, ImportedInvestorCollection);
  const firestoreQuery = query(
    collectionRef,
    where(targetField, "array-contains-any", selectedValuesWithVariants),
  );

  const snapshot = await getDocs(firestoreQuery);
  const docs = snapshot.docs;

  for (const doc of docs) {
    const data = doc.data();
    data[targetField] = data[targetField].filter(
      (val: string) => !selectedValuesWithVariants.includes(val),
    );
    data[targetField].push(...finalValues);
    await setDoc(doc.ref, data);
  }
}

export async function deleteImportedInvestorField(
  targetField: "sectorFocus" | "stageFocus",
  selectedValues: string[],
) {
  const selectedValuesWithVariants = selectedValues
    .map((val) => getAllVariantsOfSearchTerm(val))
    .flat();

  const collectionRef = collection(firestore, ImportedInvestorCollection);
  const firestoreQuery = query(
    collectionRef,
    where(targetField, "array-contains-any", selectedValuesWithVariants),
  );

  const snapshot = await getDocs(firestoreQuery);
  const docs = snapshot.docs;

  for (const doc of docs) {
    const data = doc.data();
    data[targetField] = data[targetField].filter(
      (val: string) => !selectedValuesWithVariants.includes(val),
    );
    await setDoc(doc.ref, data);
  }
}

export async function mergeImportedInvestorField(
  targetField: "sectorFocus" | "stageFocus",
  selectedValues: string[],
  finalValue: string,
  {
    progressToast,
    debugOnEmpty,
  }: { progressToast: boolean; debugOnEmpty?: boolean },
) {
  const selectedValuesWithVariants = selectedValues
    .map((val) => getAllVariantsOfSearchTerm(val))
    .flat();

  const collectionRef = collection(firestore, ImportedInvestorCollection);
  const firestoreQuery = query(
    collectionRef,
    where(targetField, "array-contains-any", selectedValuesWithVariants),
  );

  const snapshot = await getDocs(firestoreQuery);
  const docs = snapshot.docs;

  if (docs.length === 0) {
    if (debugOnEmpty) {
      window.open(
        `/admin/debug-field?targetField=${targetField}&selectedValues=${selectedValues.join(
          ",",
        )}&finalValue=${finalValue}`,
      );
    }
    throw new Error("No investors found");
  }

  if (progressToast) {
    toast.loading(
      `Merging ${selectedValues.length} ${targetField} values across ${docs.length} investors...`,
    );
  }

  // Iterate over the snapshot and update the field replacing the selected values with the final value
  for (const doc of docs) {
    const data = doc.data();

    const newFieldValues = data[targetField].map((val: string) =>
      selectedValuesWithVariants.includes(val) ||
      selectedValuesWithVariants.includes(val.toLowerCase())
        ? finalValue
        : val,
    );
    data[targetField] = Array.from(new Set(newFieldValues));
    await setDoc(doc.ref, data);
  }

  if (progressToast) {
    toast.dismiss();
  }
}

//// SEARCHABLE INVESTORS & METADATA

function mapDocToSearchableInvestorEntry(
  id: string,
  data: any,
): SearchableInvestorEntry {
  return {
    id,
    ...data,
  };
}

export async function searchForInvestors(
  vcDirectoryMetadata: VCDirectoryMetadata,
  {
    stageFocus,
    sectorFocus,
  }: {
    stageFocus?: string;
    sectorFocus?: string;
  },
  { sort = "alphabetical" }: { sort?: "alphabetical" | "relevance" } = {},
) {
  const collectionRef = collection(
    firestore,
    SearchableInvestorEntryCollection,
  );

  // Build query based on search parameters
  let firestoreQuery: Query | CollectionReference = collectionRef;

  if (stageFocus) {
    const stageFocusWeighted = vcDirectoryMetadata.stageFocusWeighted;
    const stageFocusWeightedTag = stageFocusWeighted?.find(
      (tagCountInfo) => tagCountInfo.tagKey === stageFocus,
    );

    const allStageFocusVariants = !!stageFocusWeightedTag
      ? stageFocusWeightedTag.variants
      : getAllVariantsOfSearchTerm(stageFocus);
    firestoreQuery = query(
      collectionRef,
      where("stageFocus", "array-contains-any", allStageFocusVariants),
    );
  }

  if (sectorFocus) {
    const sectorFocusWeighted = vcDirectoryMetadata.sectorFocusWeighted;
    const sectorFocusWeightedTag = sectorFocusWeighted?.find(
      (tagCountInfo) => tagCountInfo.tagKey === sectorFocus,
    );

    const allSectorFocusVariants = !!sectorFocusWeightedTag
      ? sectorFocusWeightedTag.variants
      : getAllVariantsOfSearchTerm(sectorFocus);
    firestoreQuery = query(
      collectionRef,
      where("sectorFocus", "array-contains-any", allSectorFocusVariants),
      // sort === "alphabetical" ? orderBy("name", "asc") : undefined,
    );
  }

  const snapshot = await getDocs(firestoreQuery);
  const searchResults: SearchableInvestorEntry[] = [];

  snapshot.forEach((doc) => {
    searchResults.push({
      id: doc.id,
      ...doc.data(),
    } as SearchableInvestorEntry);
  });

  if (sort === "alphabetical") {
    searchResults.sort((a, b) => a.name.localeCompare(b.name));
  }

  return searchResults;
}

export const getSearchableInvestorEntryById = async (id: string) => {
  const investorRef = doc(firestore, SearchableInvestorEntryCollection, id);
  const snapshot = await getDoc(investorRef);
  if (!snapshot.exists()) {
    return null;
  }
  return mapDocToSearchableInvestorEntry(snapshot.id, snapshot.data());
};

export const getImportedInvestorCount = async () => {
  const searchableInvestorsRef = collection(
    firestore,
    SearchableInvestorEntryCollection,
  );
  const snapshot = await getCountFromServer(searchableInvestorsRef);
  return snapshot.data().count;
};

export const getInvestorCountByRole = async (role: InvestorRole) => {
  const investorsRef = query(
    collection(firestore, SearchableInvestorEntryCollection),
    where("investorRole", "==", role),
  );
  const snapshot = await getCountFromServer(investorsRef);
  return snapshot.data().count;
};

export const getInvestorCountsForRoles = async (roles: InvestorRole[]) => {
  const counts: Map<InvestorRole, number> = new Map();
  counts.set(InvestorRole.VC, 0);
  counts.set(InvestorRole.ANGEL, 0);
  counts.set(InvestorRole.FUND, 0);
  counts.set(InvestorRole.ACCELERATOR, 0);
  counts.set(InvestorRole.INCUBATOR, 0);
  counts.set(InvestorRole.CORPORATE, 0);
  counts.set(InvestorRole.FAMILY_OFFICE, 0);
  counts.set(InvestorRole.OTHER, 0);

  for (const role of roles) {
    counts.set(role, await getInvestorCountByRole(role));
  }
  return counts;
};

export const getMostRecentVCDirectoryMetadata = async () => {
  const metadataQuery = query(
    collection(firestore, VCDirectoryMetadataCollection),
    orderBy("createdAt", "desc"),
    limit(1),
  );
  const snapshot = await getDocs(metadataQuery);
  const doc = snapshot.docs[0];
  return mapDocToVCDirectoryMetadata(doc.id, doc.data());
};
